/* Copyright (c) 2019-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable, Optional } from '@angular/core';
import { Observable, of, zip } from 'rxjs';
import { catchError, mergeMap, switchMap, map, shareReplay, take, tap } from 'rxjs/operators';
import { BrowserName, DeviceType, NetworkType, PayloadOSType, PlatformName } from '@gamestream/ragnarok';
import { Commands } from 'bridge-commands/commands';
import { Types } from 'bridge-commands/types';
import { BrowserPlugin } from 'crimson/lib/bridge/browser-plugin/browser-plugin';
import { PluginHandlers, PluginResponse } from 'crimson/lib/bridge/common/plugin.models';
import { DbService } from 'crimson/lib/db/db.service';
import { FullBridgeCommand } from 'crimson/lib/bridge/bridge.service';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import { Undefined } from 'crimson/lib/platform-detector/platform-detector';
import { SystemInfo } from 'crimson/lib/system-info-service/system-info.models';
import { generateUuid } from 'utils/lib/common/common-utils';
import { PlatformInfo, PlatformInfoService } from '../../services/platform-info.service';
import { BrowserDbNames, NetworkTypeToNetworkTypeConstantMap } from '../models';
import { SystemInfoOverrideConfig, SystemInfoPluginConfig } from './systeminfo.config';

const DeviceIdKey = 'deviceId';
const SystemInfoOverrideKey = 'systemInfoOverride';
const Systeminfo = Commands.Common.Systeminfo;

enum PlatformDetailsType {
    Payload,
    ClientHeader
}

interface SystemInfoHandlerParams {
    platformDetailsType: PlatformDetailsType;
    platformInfo: PlatformInfo;
    keys: string[];
}

@Injectable({
    providedIn: 'root'
})
export class SystemInfoPlugin extends BrowserPlugin {
    private logger: Logger;
    private overrideConfig: SystemInfoOverrideConfig;
    private isOverrideStored = false;
    private sysInfoHandlers: Map<string, (params: SystemInfoHandlerParams) => SystemInfo> = new Map();

    constructor(
        loggingService: LoggingService,
        @Optional() config: SystemInfoPluginConfig,
        private dbService: DbService,
        private platformInfoService: PlatformInfoService
    ) {
        super();
        this.logger = loggingService.getLogger('SystemInfoPlugin');
        this.logger.info('User Agent:', window.navigator.userAgent);
        this.overrideConfig = config?.systemInfoOverrideConfig ?? { allowedOSs: [], override: {} };

        this.setSysInfoHandler('DeviceType', this.getDeviceTypeHandler.bind(this));
        this.setSysInfoHandler('Platform', this.getPlatformHandler.bind(this));
        this.setSysInfoHandler('OSName', this.getOSNameHandler.bind(this));
        this.setSysInfoHandler('OSVersion', this.getOSVersionHandler.bind(this));
        this.setSysInfoHandler('BrowserName', this.getBrowserNameHandler.bind(this));
        this.setSysInfoHandler('BrowserVersion', this.getBrowserVersionHandler.bind(this));
        this.setSysInfoHandler('UserDefaultUILanguage', this.getUserDefaultUILanguageHandler.bind(this));
        this.setSysInfoHandler('ProcessorArchitecture', this.getProcessorArchitectureHandler.bind(this));
        this.setSysInfoHandler('Display', this.getDisplayHandler.bind(this));
        this.setSysInfoHandler('GPU', this.getGPUHandler.bind(this));
        this.setSysInfoHandler('Disk', this.getDiskHandler.bind(this));
        this.setSysInfoHandler('ConfidenceInBrowser', this.getConfidenceInBrowserHandler.bind(this));
        this.setSysInfoHandler('IsBrowserForged', this.getIsBrowserForgedHandler.bind(this));
        this.setSysInfoHandler('VendorName', this.getVendorNameHandler.bind(this));
        this.setSysInfoHandler('SystemName', this.getSystemNameHandler.bind(this));
        this.setSysInfoHandler('CPUName', this.getCPUNameHandler.bind(this));
        this.setSysInfoHandler('CurrentUiMode', this.getCurrentUIModeHandler.bind(this));
        this.setSysInfoHandler('UserSpecifiedDeviceName', this.getUserSpecifiedDeviceName.bind(this));
    }

    public get handlers(): PluginHandlers {
        return {
            module: Systeminfo.GetDeviceId.module,
            commands: {
                [Systeminfo.GetDeviceId.method]: this.getDeviceId.bind(this),
                [Systeminfo.GetSystemInfo.method]: this.getSystemInfo.bind(this),
                [Systeminfo.GetClientHeadersInfo.method]: this.getClientHeadersInfo.bind(this),
                [Systeminfo.GetStreamingSupportInfo.method]: this.isStreamingSupported.bind(this),
                [Systeminfo.GetNetworkTypeChangedInfo.method]: this.onNetworkTypeChanged.bind(this)
            }
        };
    }

    private isStreamingSupported(command: FullBridgeCommand<{}>): Observable<PluginResponse> {
        return this.platformInfoService.isBrowserStreamingSupported().pipe(
            take(1),
            mergeMap((isSupported: boolean) =>
                this.makeResult(
                    JSON.stringify({
                        IsStreamingSupported: isSupported
                    })
                )
            )
        );
    }

    private setSysInfoHandler(key: keyof SystemInfo, handlerFn: (params: SystemInfoHandlerParams) => SystemInfo): void {
        this.sysInfoHandlers.set(key, handlerFn);
    }

    private makeSystemInfoData(params: SystemInfoHandlerParams): SystemInfo {
        const systemInfo: SystemInfo = {};
        const nonImplementedKeys = [];

        if (params.keys?.length) {
            params.keys.forEach(key => {
                const handler = this.sysInfoHandlers.get(key);
                if (handler) {
                    systemInfo[key] = handler(params)[key];
                } else {
                    nonImplementedKeys.push(key);
                }
            });
        }

        if (nonImplementedKeys.length) {
            this.logger.info(`SystemInfo not implemented for keys ${JSON.stringify(nonImplementedKeys)}`);
        }

        return systemInfo;
    }

    private getDeviceId(command: FullBridgeCommand<{}>): Observable<PluginResponse> {
        // Try reading deviceId from DB. If it does not exist, create one and return.
        return this.readDeviceId().pipe(
            mergeMap((deviceId: string) => {
                if (!deviceId) {
                    const deviceIdObj = { DeviceID: generateUuid() };
                    return this.storeDeviceId(deviceIdObj.DeviceID).pipe(
                        switchMap(_ => this.makeResult(JSON.stringify(deviceIdObj)))
                    );
                } else {
                    const deviceIdObj = { DeviceID: deviceId };
                    return this.makeResult(JSON.stringify(deviceIdObj));
                }
            }),
            catchError(err => this.makeError(500, 'Failed to get device id: ' + err.toString()))
        );
    }

    protected getClientHeadersInfo(): Observable<PluginResponse> {
        const keys: string[] = [];
        this.sysInfoHandlers.forEach((_, key) => {
            keys.push(key);
        });
        return this.fetchSystemInfo(PlatformDetailsType.ClientHeader, keys);
    }

    private getSystemInfo(
        command: FullBridgeCommand<Types.Common.Systeminfo.GetSystemInfoParameters>
    ): Observable<PluginResponse> {
        return this.fetchSystemInfo(PlatformDetailsType.Payload, command.payload?.keys);
    }

    private fetchSystemInfo(platformDetailsType: PlatformDetailsType, keys: string[]) {
        const fetchPlatformInfo$ = this.platformInfoService
            .getPlatformInfo()
            .pipe(map(platformInfo => this.makeSystemInfoData({ platformDetailsType, platformInfo, keys })));

        const systemInfo$ = zip(
            keys.includes('Network') ? of(this.getNetworkSystemInfo()) : of({}),
            keys.some(k => k !== 'Network') ? fetchPlatformInfo$ : of({})
        ).pipe(
            map(([network, other]) => {
                return { ...network, ...other };
            })
        );

        const override$ = this.platformInfoService.getPlatformInfo().pipe(
            mergeMap(platformInfo => {
                const deviceOS = this.platformInfoService.getTranslatedPayloadOsType(platformInfo.details);
                return this.getSystemInfoOverride(deviceOS, keys);
            })
        );

        return zip(systemInfo$, override$).pipe(
            take(1),
            mergeMap(([systemInfo, override]) => {
                Object.assign(systemInfo, override);
                return this.makeResult(JSON.stringify(systemInfo));
            }),
            shareReplay(1)
        );
    }

    private getNetworkSystemInfo(): SystemInfo {
        return {
            Network: {
                Fingerprint: '',
                NetworkType: this.getMappedNetworkType(this.platformInfoService.getNetworkType())
            }
        };
    }

    private onNetworkTypeChanged(): Observable<PluginResponse> {
        return this.platformInfoService
            .onNetworkTypeChanged()
            .pipe(
                mergeMap((networkType: NetworkType) =>
                    this.makeResult(JSON.stringify({ networkType: this.getMappedNetworkType(networkType) }))
                )
            );
    }

    private readDeviceId(): Observable<string> {
        return this.dbService.getGlobalStore(BrowserDbNames.Webclient).getItem(DeviceIdKey);
    }

    private storeDeviceId(deviceId: string): Observable<string> {
        return this.dbService.getGlobalStore(BrowserDbNames.Webclient).setItem(DeviceIdKey, deviceId);
    }

    private getDeviceTypeHandler(params: SystemInfoHandlerParams): SystemInfo {
        const platformDetailsType = params.platformDetailsType;
        const platformInfo = params.platformInfo;
        return {
            DeviceType:
                platformDetailsType === PlatformDetailsType.ClientHeader
                    ? this.platformInfoService.getTranslatedClientHeaderDeviceType(platformInfo.details)
                    : this.platformInfoService.getTranslatedPayloadDeviceType(platformInfo.details)
        };
    }

    private getPlatformHandler(params: SystemInfoHandlerParams): SystemInfo {
        return {
            Platform: params.platformInfo.details.os
        };
    }

    private getOSNameHandler(params: SystemInfoHandlerParams): SystemInfo {
        const platformDetailsType = params.platformDetailsType;
        const platformInfo = params.platformInfo;
        return {
            OSName:
                platformDetailsType === PlatformDetailsType.ClientHeader
                    ? this.platformInfoService.getTranslatedClientHeaderOsType(platformInfo.details)
                    : this.platformInfoService.getTranslatedPayloadOsType(platformInfo.details),
            OSVersion: this.getOSVersionHandler(params).OSVersion
        };
    }

    private getOSVersionHandler(params: SystemInfoHandlerParams): SystemInfo {
        const platformInfo = params.platformInfo;
        return {
            OSVersion:
                platformInfo.details.osVer && platformInfo.details.osVer !== '0'
                    ? platformInfo.details.osVer
                    : Undefined
        };
    }

    private getBrowserNameHandler(params: SystemInfoHandlerParams): SystemInfo {
        const platformDetailsType = params.platformDetailsType;
        const platformInfo = params.platformInfo;
        return {
            BrowserName:
                platformDetailsType === PlatformDetailsType.ClientHeader
                    ? this.platformInfoService.getTranslatedClientHeaderBrowserType(platformInfo.details)
                    : this.platformInfoService.getTranslatedPayloadBrowserType(platformInfo.details)
        };
    }

    private getBrowserVersionHandler(params: SystemInfoHandlerParams): SystemInfo {
        const platformInfo = params.platformInfo;
        if (platformInfo.details.browserFullVer === '0') {
            return { BrowserVersion: platformInfo.details.browserVer || Undefined };
        } else {
            return {
                BrowserVersion: platformInfo.details.browserFullVer || platformInfo.details.browserVer || Undefined
            };
        }
    }

    private getUserDefaultUILanguageHandler(params: SystemInfoHandlerParams): SystemInfo {
        /**
         * Marketing has requested a change in the Browser Language Fallback Logic.
         * This is a deviation from the standards. Details are present in MVPC-13862.
         */
        const defaultUiLanguage: SystemInfo = {
            UserDefaultUILanguage: this.sanitizeLanguage(
                window.navigator.language?.replace('-', '_'),
                params.platformInfo.details.browser === BrowserName.SAFARI
            )
        };
        this.logger.info(
            'preferred languages are',
            window.navigator.languages,
            'default pick is',
            defaultUiLanguage.UserDefaultUILanguage
        );
        return defaultUiLanguage;
    }

    private getProcessorArchitectureHandler(params: SystemInfoHandlerParams): SystemInfo {
        return {
            ProcessorArchitecture: params.platformInfo.processorArchitecture || undefined
        };
    }

    private getDisplayHandler(): SystemInfo {
        const LogicalResolution = window.screen.width.toString() + ' x ' + window.screen.height.toString();
        const PhysicalResolution =
            Math.floor(window.screen.width * window.devicePixelRatio) +
            ' x ' +
            Math.floor(window.screen.height * window.devicePixelRatio);
        return {
            Display: [
                {
                    RefreshRate: '',
                    LogicalResolution,
                    PhysicalResolution
                }
            ]
        };
    }

    private getGPUHandler(params: SystemInfoHandlerParams): SystemInfo {
        return {
            GPU: { GPUData: [{ LongGPUName: params.platformInfo.details.gpuDetail }] }
        };
    }

    private getDiskHandler(): SystemInfo {
        return {
            Disk: []
        };
    }

    private getConfidenceInBrowserHandler(params: SystemInfoHandlerParams): SystemInfo {
        return {
            ConfidenceInBrowser: params.platformInfo.details.confidence
        };
    }

    private getIsBrowserForgedHandler(params: SystemInfoHandlerParams): SystemInfo {
        return {
            IsBrowserForged: params.platformInfo.details.forging
        };
    }

    private getVendorNameHandler(params: SystemInfoHandlerParams): SystemInfo {
        return {
            VendorName: params.platformInfo.details.vendor || undefined
        };
    }

    private getSystemNameHandler(params: SystemInfoHandlerParams): SystemInfo {
        return {
            SystemName: params.platformInfo.systemName || undefined
        };
    }

    private getCPUNameHandler(params: SystemInfoHandlerParams): SystemInfo {
        return {
            CPUName: params.platformInfo.cpuName || undefined
        };
    }

    private getSystemInfoOverride(deviceOS: PayloadOSType, keys: string[]): Observable<SystemInfo> {
        if (!this.overrideConfig.allowedOSs.includes(deviceOS)) {
            return of({});
        }

        const keyBasedMappingOperator = map(override => {
            let retVal = {};
            if (override) {
                if (keys?.length) {
                    keys.forEach(key => {
                        if (override[key]) {
                            retVal[key] = override[key];
                        }
                    });
                } else {
                    retVal = override;
                }
            }
            return retVal;
        });

        const overrideInConfig = this.overrideConfig.override;
        if (overrideInConfig && Object.keys(overrideInConfig).length > 0) {
            this.storeSystemInfoOverrideIfNeeded(overrideInConfig);
            return of(overrideInConfig).pipe(keyBasedMappingOperator);
        }

        return this.readSystemInfoOverride().pipe(
            tap(override => {
                if (override) {
                    this.logger.info('Override is loaded from DB');
                    this.overrideConfig.override = override;
                }
            }),
            keyBasedMappingOperator
        );
    }

    private storeSystemInfoOverrideIfNeeded(override: SystemInfo) {
        if (!this.isOverrideStored) {
            this.dbService
                .getGlobalStore(BrowserDbNames.Webclient)
                .setItem(SystemInfoOverrideKey, override)
                .pipe(
                    take(1),
                    tap(_ => {
                        this.logger.info('Override is stored in DB');
                        this.isOverrideStored = true;
                    })
                )
                .subscribe();
        }
    }

    private readSystemInfoOverride(): Observable<SystemInfo> {
        return this.dbService.getGlobalStore(BrowserDbNames.Webclient).getItem(SystemInfoOverrideKey);
    }

    private sanitizeLanguage(language: string, isSafari: boolean): string {
        if (isSafari) {
            language = language.replace(
                /(.*[-_])([a-zA-Z]*)/,
                (_substring, p1: string, p2: string) => `${p1}${p2.toUpperCase()}`
            );
        }
        return language;
    }

    /**
     * Mapping Ragnarok's `NetworkType` to generic `NetworkTypeConstants` in SystemInfo
     */
    private getMappedNetworkType(networkType: NetworkType): string {
        // TODO: Ideally, we should just return Ragnarok's `NetworkType` enum as
        // telemetry strings to reduce the maintaining work if `NetworkType` enum adds /removes new key.
        const type = NetworkTypeToNetworkTypeConstantMap[networkType];
        if (!type) {
            this.logger.error(
                `Invalid case, unable to match NetworkType: ${networkType} from Ragnarok with current network constants`
            );
            return undefined;
        }
        return type;
    }

    private getCurrentUIModeHandler(params: SystemInfoHandlerParams): SystemInfo {
        const platformInfo = params.platformInfo;
        const deviceType = platformInfo.details.deviceType;
        let currentUiMode = Types.Common.Systeminfo.UiModeEnum.Desktop;
        if (deviceType) {
            if (deviceType === DeviceType.PHONE || deviceType === DeviceType.TABLET) {
                currentUiMode = Types.Common.Systeminfo.UiModeEnum.Mobile;
            }
        } else {
            if (
                platformInfo.details.os === PlatformName.ANDROID ||
                platformInfo.details.os === PlatformName.IOS ||
                platformInfo.details.os === PlatformName.IPADOS
            ) {
                currentUiMode = Types.Common.Systeminfo.UiModeEnum.Mobile;
            }
        }

        return {
            CurrentUiMode: currentUiMode
        };
    }

    private getUserSpecifiedDeviceName(params: SystemInfoHandlerParams): SystemInfo {
        // We are not able to retrieve user-specified device name on browser clients
        // due to API limitations.
        return {
            UserSpecifiedDeviceName: ''
        };
    }
}
